"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calculator,
  Check,
  Sparkles,
  TrendingUp,
  ShieldCheck,
  DollarSign,
  Users,
  Database,
  Layers,
  MessageSquare,
  Copy,
  CheckCircle2,
  ArrowRight,
  Zap,
  RefreshCw,
  Server,
  Building,
  CreditCard,
  Cpu,
  Smartphone
} from "lucide-react";
import { Card } from "@/components/ui/Card";

interface PlatformOption {
  id: string;
  name: string;
  desc: string;
  icon: React.ElementType;
  baseInr: number;
  baseUsd: number;
  typicalSaaSMonthlyInr: number;
  typicalSaaSMonthlyUsd: number;
  timelineWeeks: string;
}

const PLATFORM_OPTIONS: PlatformOption[] = [
  {
    id: "crm",
    name: "Custom CRM & Sales Pipeline",
    desc: "Lead ingestion, WhatsApp bot, pipeline stages, broker commissions & team tracking.",
    icon: Users,
    baseInr: 240000,
    baseUsd: 2900,
    typicalSaaSMonthlyInr: 6500, // vs Salesforce / HubSpot ~$80/mo
    typicalSaaSMonthlyUsd: 80,
    timelineWeeks: "3–4 weeks",
  },
  {
    id: "erp",
    name: "Enterprise ERP & Operations",
    desc: "Inventory registers, multi-warehouse, purchase orders, GST billing & dispatch.",
    icon: Database,
    baseInr: 450000,
    baseUsd: 5400,
    typicalSaaSMonthlyInr: 9500, // vs NetSuite / SAP Business One ~$115/mo
    typicalSaaSMonthlyUsd: 115,
    timelineWeeks: "5–7 weeks",
  },
  {
    id: "webapp",
    name: "Web Application & Client Portal",
    desc: "Next.js frontend, PostgreSQL backend, role-based dashboards & sub-second performance.",
    icon: Layers,
    baseInr: 200000,
    baseUsd: 2400,
    typicalSaaSMonthlyInr: 5000,
    typicalSaaSMonthlyUsd: 60,
    timelineWeeks: "3–5 weeks",
  },
  {
    id: "mobile",
    name: "Mobile App (iOS & Android)",
    desc: "Cross-platform React Native, offline synchronization, push notifications & biometric auth.",
    icon: Smartphone,
    baseInr: 320000,
    baseUsd: 3900,
    typicalSaaSMonthlyInr: 7000,
    typicalSaaSMonthlyUsd: 85,
    timelineWeeks: "4–6 weeks",
  },
  {
    id: "vertical",
    name: "Industry Suite (Clinic/Gym/Logistics)",
    desc: "Tailored for specific Indian regulatory requirements (DPDP EMR, FEFO drugs, GPS telematics).",
    icon: Building,
    baseInr: 360000,
    baseUsd: 4400,
    typicalSaaSMonthlyInr: 7500,
    typicalSaaSMonthlyUsd: 90,
    timelineWeeks: "4–6 weeks",
  },
];

interface FeatureAddon {
  id: string;
  name: string;
  desc: string;
  inr: number;
  usd: number;
  icon: React.ElementType;
}

const FEATURE_ADDONS: FeatureAddon[] = [
  {
    id: "whatsapp",
    name: "Official WhatsApp Cloud Business API",
    desc: "Automated instant lead response, catalog dispatches & renewal reminders.",
    inr: 45000,
    usd: 550,
    icon: MessageSquare,
  },
  {
    id: "payment",
    name: "Payment Gateway & Automated Reconciliation",
    desc: "Razorpay / Stripe integration, recurring subscriptions & automated GST invoices.",
    inr: 35000,
    usd: 450,
    icon: CreditCard,
  },
  {
    id: "hardware",
    name: "Hardware, Biometrics or GPS Telematics",
    desc: "eSSL / ZKTeco turnstile sync, thermal POS printers or vehicle GPS sensors.",
    inr: 65000,
    usd: 800,
    icon: Server,
  },
  {
    id: "ai",
    name: "AI Copilot & Workflow Automation",
    desc: "Private RAG document querying, automated lead classification & prompt triage.",
    inr: 85000,
    usd: 1050,
    icon: Cpu,
  },
  {
    id: "multitenant",
    name: "Multi-Branch & Multi-Tenant Isolation",
    desc: "Separate database schemas per branch with consolidated HQ analytics.",
    inr: 75000,
    usd: 900,
    icon: Building,
  },
  {
    id: "audit",
    name: "Compliance Audit Trail & Enterprise RBAC",
    desc: "Granular permission sets, IP whitelisting & tamper-evident audit logs.",
    inr: 40000,
    usd: 500,
    icon: ShieldCheck,
  },
];

export const SoftwareCostCalculator: React.FC = () => {
  const [currency, setCurrency] = React.useState<"INR" | "USD">("INR");
  const [selectedPlatform, setSelectedPlatform] = React.useState<string>("crm");
  const [teamSize, setTeamSize] = React.useState<number>(20);
  const [selectedAddons, setSelectedAddons] = React.useState<string[]>([
    "whatsapp",
    "payment",
  ]);
  const [copied, setCopied] = React.useState(false);

  // Auto-detect currency based on visitor timezone
  React.useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (
        tz &&
        (tz.includes("Kolkata") ||
          tz.includes("Calcutta") ||
          tz.includes("India") ||
          tz.includes("Asia/Colombo"))
      ) {
        setCurrency("INR");
      } else {
        setCurrency("USD");
      }
    } catch {
      setCurrency("INR");
    }
  }, []);

  const currentPlatform =
    PLATFORM_OPTIONS.find((p) => p.id === selectedPlatform) ||
    PLATFORM_OPTIONS[0];

  const toggleAddon = (addonId: string) => {
    setSelectedAddons((prev) =>
      prev.includes(addonId)
        ? prev.filter((id) => id !== addonId)
        : [...prev, addonId]
    );
  };

  // Calculations
  const isInr = currency === "INR";
  const basePrice = isInr
    ? currentPlatform.baseInr
    : currentPlatform.baseUsd;

  const addonsPrice = selectedAddons.reduce((sum, id) => {
    const addon = FEATURE_ADDONS.find((a) => a.id === id);
    if (!addon) return sum;
    return sum + (isInr ? addon.inr : addon.usd);
  }, 0);

  const customBuildTotal = basePrice + addonsPrice;

  // Milestone breakdown
  const phase1Discovery = Math.round(customBuildTotal * 0.2);
  const phase2Core = Math.round(customBuildTotal * 0.4);
  const phase3Launch = customBuildTotal - phase1Discovery - phase2Core;

  // SaaS Rent Trap Comparison (36 Months)
  const monthlyPerSeat = isInr
    ? currentPlatform.typicalSaaSMonthlyInr
    : currentPlatform.typicalSaaSMonthlyUsd;

  // 3-year recurring cost with typical 15% platform/storage add-on fee
  const saas3YearTotal = Math.round(
    teamSize * monthlyPerSeat * 36 * 1.15
  );

  const net3YearSavings = Math.max(0, saas3YearTotal - customBuildTotal);
  const breakevenMonths = Math.min(
    36,
    Math.max(
      1,
      Math.round(customBuildTotal / (teamSize * monthlyPerSeat || 1))
    )
  );

  const formatPrice = (amount: number) => {
    if (isInr) {
      return `₹${amount.toLocaleString("en-IN")}`;
    }
    return `$${amount.toLocaleString("en-US")}`;
  };

  const generateScopeSummary = () => {
    const activeAddonsList = FEATURE_ADDONS.filter((a) =>
      selectedAddons.includes(a.id)
    )
      .map((a) => `• ${a.name}`)
      .join("\n");

    return `*Johnson Softwares - Custom Software Scope Estimate*\n` +
      `-----------------------------------------\n` +
      `System: ${currentPlatform.name}\n` +
      `Team Size: ${teamSize} Active Users / Seats\n` +
      `Target Delivery: ${currentPlatform.timelineWeeks}\n` +
      `Selected Integrations:\n${activeAddonsList || "• Standard Architecture"}\n\n` +
      `Estimated One-Time Build: ${formatPrice(customBuildTotal)} (Zero per-seat licensing)\n` +
      `3-Year Traditional SaaS Spend: ${formatPrice(saas3YearTotal)}\n` +
      `*Estimated 3-Year Net Savings: ${formatPrice(net3YearSavings)}*\n` +
      `Breakeven Window: ~${breakevenMonths} months\n` +
      `Full Source Code & Database Ownership: Yes (100% IP transfer)`;
  };

  const handleCopyScope = () => {
    navigator.clipboard.writeText(generateScopeSummary());
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const whatsappMessage = encodeURIComponent(
    `Hello Johnson Softwares team, I just calculated my project scope on your website:\n\n${generateScopeSummary()}\n\nI'd like to schedule an architectural review.`
  );

  return (
    <div className="w-full space-y-12">
      {/* Top Controller: Currency & Mode Toggle */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 rounded-xl bg-slate-900/40 dark:bg-slate-900/60 border border-border-muted/50 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary animate-pulse" />
          <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
            Interactive TCO & Pricing Engine (2026)
          </span>
        </div>

        <div className="flex items-center gap-2 bg-slate-950/80 p-1 rounded-lg border border-border-muted">
          <span className="text-xs font-medium px-2 text-text-muted">Currency:</span>
          <button
            onClick={() => setCurrency("INR")}
            className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
              isInr
                ? "bg-primary text-slate-950 shadow-sm"
                : "text-text-muted hover:text-foreground"
            }`}
          >
            INR (₹)
          </button>
          <button
            onClick={() => setCurrency("USD")}
            className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
              !isInr
                ? "bg-primary text-slate-950 shadow-sm"
                : "text-text-muted hover:text-foreground"
            }`}
          >
            USD ($)
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Configurator (7 Cols) */}
        <div className="lg:col-span-7 space-y-8">
          {/* Step 1: Select Platform */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-bold font-heading text-foreground flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-extrabold">
                  1
                </span>
                Select Software Solution Type
              </label>
              <span className="text-xs text-primary font-semibold">
                Est. {currentPlatform.timelineWeeks}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PLATFORM_OPTIONS.map((platform) => {
                const Icon = platform.icon;
                const isSelected = selectedPlatform === platform.id;
                return (
                  <button
                    key={platform.id}
                    onClick={() => setSelectedPlatform(platform.id)}
                    className={`p-4 rounded-xl text-left border transition-all relative ${
                      isSelected
                        ? "bg-primary/5 dark:bg-primary/10 border-primary shadow-sm"
                        : "bg-white/40 dark:bg-slate-900/40 border-border-muted/60 hover:border-border-muted"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`p-2 rounded-lg shrink-0 ${
                          isSelected
                            ? "bg-primary text-slate-950"
                            : "bg-slate-800/20 dark:bg-slate-800 text-primary"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-sm font-bold text-foreground">
                          {platform.name}
                        </div>
                        <div className="text-xs text-text-muted mt-1 line-clamp-2">
                          {platform.desc}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 2: Team Size / Active Users Slider */}
          <div className="space-y-4 p-5 rounded-2xl bg-white/40 dark:bg-slate-900/40 border border-border-muted/60 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <label className="text-sm font-bold font-heading text-foreground flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-extrabold">
                  2
                </span>
                Team Size / Active Users
              </label>
              <span className="text-base font-extrabold text-primary bg-primary/10 px-3 py-1 rounded-full">
                {teamSize} Users
              </span>
            </div>

            <p className="text-xs text-text-muted">
              Commercial SaaS providers charge per-seat monthly fees (e.g. ₹6,500/user/mo). Custom software gives you unlimited users with zero incremental per-seat licenses.
            </p>

            <input
              type="range"
              min="5"
              max="200"
              step="5"
              value={teamSize}
              onChange={(e) => setTeamSize(Number(e.target.value))}
              className="w-full h-2 bg-slate-200 dark:bg-slate-800 rounded-lg appearance-none cursor-pointer accent-primary"
            />

            <div className="flex justify-between text-[11px] font-semibold text-text-muted">
              <span>5 users (Boutique)</span>
              <span>25 users</span>
              <span>50 users</span>
              <span>100 users</span>
              <span>200+ users (Enterprise)</span>
            </div>
          </div>

          {/* Step 3: Modular Capabilities & Integrations */}
          <div className="space-y-3">
            <label className="text-sm font-bold font-heading text-foreground flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-extrabold">
                3
              </span>
              Modular Capabilities & Architectural Addons
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {FEATURE_ADDONS.map((addon) => {
                const Icon = addon.icon;
                const isChecked = selectedAddons.includes(addon.id);
                return (
                  <button
                    key={addon.id}
                    onClick={() => toggleAddon(addon.id)}
                    className={`p-3.5 rounded-xl text-left border transition-all flex items-start gap-3 ${
                      isChecked
                        ? "bg-primary/5 dark:bg-primary/10 border-primary/80 shadow-xs"
                        : "bg-white/40 dark:bg-slate-900/40 border-border-muted/60 hover:border-border-muted"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 mt-0.5 border ${
                        isChecked
                          ? "bg-primary border-primary text-slate-950"
                          : "border-border-muted bg-transparent"
                      }`}
                    >
                      {isChecked && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-bold text-foreground">
                          {addon.name}
                        </span>
                        <span className="text-[11px] font-mono font-semibold text-primary shrink-0">
                          +{formatPrice(isInr ? addon.inr : addon.usd)}
                        </span>
                      </div>
                      <p className="text-[11px] text-text-muted mt-0.5">
                        {addon.desc}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Column: Live Calculation & SaaS Comparison (5 Cols) */}
        <div className="lg:col-span-5 space-y-6">
          <div className="sticky top-24 space-y-6">
            {/* Custom Build Total Card */}
            <Card
              className="p-6 rounded-2xl bg-gradient-to-br from-slate-900/90 to-slate-950/90 border-primary/30 relative overflow-hidden shadow-2xl backdrop-blur-md"
              glowColor="rgba(6, 182, 212, 0.2)"
            >
              <div className="absolute top-0 right-0 w-48 h-48 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

              <div className="space-y-5 relative z-10">
                <div className="flex items-center justify-between pb-4 border-b border-border-muted/40">
                  <div>
                    <span className="text-[10px] uppercase font-bold tracking-widest text-primary bg-primary/10 px-2.5 py-0.5 rounded-full">
                      Zero Per-Seat License
                    </span>
                    <h3 className="font-heading font-extrabold text-xl text-foreground mt-1">
                      One-Time Build Estimate
                    </h3>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-heading font-black text-foreground tracking-tight">
                      {formatPrice(customBuildTotal)}
                    </div>
                    <span className="text-[11px] text-text-muted">
                      100% IP & Code Ownership
                    </span>
                  </div>
                </div>

                {/* Milestone Breakdown */}
                <div className="space-y-2.5">
                  <span className="text-xs font-bold uppercase tracking-wider text-text-muted block">
                    Transparent Milestone Roadmap:
                  </span>

                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between p-2 rounded-lg bg-slate-800/40 border border-border-muted/30">
                      <span className="text-text-muted flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                        Phase 1: Architecture Blueprint (20%)
                      </span>
                      <span className="font-mono font-bold text-foreground">
                        {formatPrice(phase1Discovery)}
                      </span>
                    </div>

                    <div className="flex justify-between p-2 rounded-lg bg-slate-800/40 border border-border-muted/30">
                      <span className="text-text-muted flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                        Phase 2: Core Data Schema & APIs (40%)
                      </span>
                      <span className="font-mono font-bold text-foreground">
                        {formatPrice(phase2Core)}
                      </span>
                    </div>

                    <div className="flex justify-between p-2 rounded-lg bg-slate-800/40 border border-border-muted/30">
                      <span className="text-text-muted flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                        Phase 3: Integrations & Handover (40%)
                      </span>
                      <span className="font-mono font-bold text-foreground">
                        {formatPrice(phase3Launch)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 3-Year SaaS Tax Comparison */}
                <div className="p-4 rounded-xl bg-danger/10 border border-danger/20 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-danger flex items-center gap-1.5">
                      <TrendingUp className="w-3.5 h-3.5" />
                      Traditional SaaS 3-Year Rent:
                    </span>
                    <span className="font-mono font-bold text-danger text-sm">
                      {formatPrice(saas3YearTotal)}
                    </span>
                  </div>
                  <p className="text-[11px] text-text-muted leading-relaxed">
                    Paying monthly fees for {teamSize} users on Salesforce/HubSpot adds up to {formatPrice(saas3YearTotal)} with zero equity or code ownership.
                  </p>
                </div>

                {/* Net Savings & Payback Callout */}
                <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-between">
                  <div>
                    <span className="text-xs font-bold text-green-600 dark:text-green-400 block">
                      3-Year Net Business Savings:
                    </span>
                    <span className="text-[11px] text-text-muted">
                      Breakeven achieved in ~{breakevenMonths} months
                    </span>
                  </div>
                  <div className="text-xl font-heading font-black text-green-600 dark:text-green-400">
                    +{formatPrice(net3YearSavings)}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2.5 pt-2">
                  <a
                    href={`https://wa.me/919880893416?text=${whatsappMessage}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary text-slate-950 font-heading font-bold text-sm hover:bg-primary-hover transition-all shadow-lg shadow-primary/20"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Discuss Scope on WhatsApp
                  </a>

                  <div className="flex gap-2">
                    <button
                      onClick={handleCopyScope}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800/80 hover:bg-slate-800 text-xs font-bold text-foreground border border-border-muted transition-all"
                    >
                      {copied ? (
                        <>
                          <Check className="w-3.5 h-3.5 text-green-400" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          Copy Scope
                        </>
                      )}
                    </button>

                    <a
                      href="/contact"
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800/80 hover:bg-slate-800 text-xs font-bold text-primary border border-border-muted transition-all"
                    >
                      Book Review
                      <ArrowRight className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>

                <div className="pt-2 text-center text-[10px] text-text-muted">
                  Includes mutual NDA, 100% intellectual property transfer, and Git repository handover.
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
